import { Injectable, Injector, Type } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  DwEventService,
  DwMetaDataFormPreferenceHierarchy,
  DwUiConfigPanelComponent,
  DwUiConfigService,
  DwUiConfigType,
  DwUiPanelWidgetConfig,
  deepCopy
} from '@devwareapps/devware-cap';
import { QUESTION_FORM_CONFIG_KEY, QUESTION_FORM_LAYOUT_WIDGET_KEY, QuestionFormLayoutVariant } from '../models/question-form-layout.model';

export interface QuestionFormLayoutState {
  /** Question form preference hierarchy this session works from (updated after Save). */
  hierarchy?: DwMetaDataFormPreferenceHierarchy;
  savedLabel: string;
  previewLabel: string;
  message: string;
  busy: boolean;
}

/**
 * Owns the Question Bank layout switch that appears in the CAP gear panel as
 * "Question-layout (Form)".
 *
 * Registration: whenever the Question metadata form registers its own panel
 * widget, this adds the layout widget next to it.
 *
 * Applying a variant publishes the form's preference hierarchy with the
 * variant's section tree on the same event channel the designer uses. That
 * rebuilds the form, which re-registers its embedded grids with the panel.
 * Stock Devware then re-selects the panel's first widget (see
 * keepPanelSelection), which would drop this widget mid-preview, so the state
 * lives here rather than in the component.
 */
@Injectable({ providedIn: 'root' })
export class QuestionFormLayoutWidgetRegistrationService {
  readonly state: QuestionFormLayoutState = { savedLabel: '', previewLabel: '', message: '', busy: false };

  private registered = false;
  protected activeFormConfigKey: string;
  private widgetComponent!: Type<any>;
  private sourceData: any;
  /** ModifiedDateTime the live form loaded with; publishes must carry it or the form ignores them. */
  private formTimestamp?: string;

  protected uiConfigService!: DwUiConfigService;
  private dwEventService!: DwEventService;
  private http!: HttpClient;

  protected readonly formConfigKey: string = QUESTION_FORM_CONFIG_KEY;
  protected readonly layoutWidgetKey: string = QUESTION_FORM_LAYOUT_WIDGET_KEY;
  protected readonly layoutClass: string = 'qb-layout';

  protected get formConfigKeys(): string[] { return [this.formConfigKey]; }

  constructor(private readonly injector: Injector) { }

  protected loadLayout(variant: QuestionFormLayoutVariant): Observable<{ section: any }> {
    return this.http.get<{ section: any }>(variant.asset);
  }

  /** @param widgetComponent the panel widget to show; passed in to avoid a component/service import cycle. */
  register(widgetComponent: Type<any>): void {
    if (this.registered) {
      return;
    }
    this.registered = true;
    this.widgetComponent = widgetComponent;
    // DwUiConfigInit methods run from inside DwUiConfigService's constructor, so
    // Devware services can only be resolved once that constructor has returned.
    setTimeout(() => {
      this.uiConfigService = this.injector.get(DwUiConfigService);
      this.dwEventService = this.injector.get(DwEventService);
      this.http = this.injector.get(HttpClient);
      this.keepPanelSelection();
      this.uiConfigService.getUiPanelWidgetConfigs().subscribe(configs => this.onWidgetConfigs(configs));
    });
  }

  /**
   * Devware's panel re-runs determineWidgetConfig on every widget (re)registration
   * and always lands on widgetConfigs[0]; its own "keep the current widget"
   * branch is commented out. Embedded grids re-register whenever the form
   * rebuilds, so without this the panel bounces off the layout widget (and off
   * any non-first widget) the moment a preview is applied. Keep the current
   * selection while it still exists; fall through to stock behaviour otherwise.
   */
  private keepPanelSelection(): void {
    const proto = DwUiConfigPanelComponent.prototype as any;
    if (proto.__aviatorKeepSelection) {
      return;
    }
    proto.__aviatorKeepSelection = true;
    const original = proto.determineWidgetConfig;
    proto.determineWidgetConfig = function (this: any): void {
      const current = this.currentWidgetConfig;
      const stillListed = current && (this.widgetConfigs || []).some(
        (w: DwUiPanelWidgetConfig) => w.configKey === current.configKey && w.uiConfigTypeId === current.uiConfigTypeId);
      if (stillListed) {
        this.hasConfigurations = true;
        return;
      }
      original.call(this);
    };
  }

  apply(variant: QuestionFormLayoutVariant, persist: boolean): void {
    const state = this.state;
    if (!state.hierarchy || state.busy) {
      return;
    }
    state.busy = true;
    state.message = '';
    this.loadLayout(variant).subscribe(
      config => {
        const next = deepCopy(state.hierarchy) as DwMetaDataFormPreferenceHierarchy;
        next.loadedPreferences = { ...(next.loadedPreferences || {}), section: config.section } as any;
        if (!persist) {
          this.publish(next);
          state.previewLabel = variant.label;
          state.message = `Previewing ${variant.label} layout`;
          state.busy = false;
          return;
        }
        this.uiConfigService.savePreferencesHierarchy(next).subscribe(
          saved => {
            state.hierarchy = saved;
            this.publish(saved);
            state.savedLabel = variant.label;
            state.previewLabel = '';
            state.message = `${variant.label} layout saved`;
            state.busy = false;
          },
          () => {
            state.message = `Could not save the ${variant.label} layout`;
            state.busy = false;
          }
        );
      },
      () => {
        state.message = `Could not load ${variant.asset}`;
        state.busy = false;
      }
    );
  }

  private onWidgetConfigs(configs: DwUiPanelWidgetConfig[]): void {
    const formWidget = configs.find(c => this.formConfigKeys.includes(c.configKey) && c.uiConfigTypeId === DwUiConfigType.form);
    if (!formWidget) {
      return;
    }
    if (formWidget.data !== this.sourceData) {
      // A new config session on the Question form: start from its hierarchy.
      this.activeFormConfigKey = formWidget.configKey;
      this.sourceData = formWidget.data;
      this.state.hierarchy = deepCopy(formWidget.data);
      this.state.savedLabel = this.variantLabelFor(this.state.hierarchy);
      this.state.previewLabel = '';
      this.state.message = '';
      this.formTimestamp = this.state.hierarchy.configData && this.state.hierarchy.configData.ModifiedDateTime;
    }
    if (!configs.some(c => c.configKey === this.layoutWidgetKey)) {
      const widget = new DwUiPanelWidgetConfig();
      widget.configKey = this.layoutWidgetKey;
      widget.uiConfigTypeId = DwUiConfigType.form;
      widget.widgetComponent = this.widgetComponent;
      this.uiConfigService.registerUiPanelWidget(widget);
    }
  }

  /** Same channel the designer uses; the live Question form re-renders from it. */
  private publish(hierarchy: DwMetaDataFormPreferenceHierarchy): void {
    const copy = deepCopy(hierarchy) as DwMetaDataFormPreferenceHierarchy;
    // The form drops updates whose config row is newer than the one it loaded,
    // so keep publishing with the timestamp it already holds.
    if (copy.configData && this.formTimestamp) {
      copy.configData.ModifiedDateTime = this.formTimestamp;
    }
    // Finalized keeps the panel's copy in step so the Question widget, when the
    // panel jumps to it, does not republish the previous layout.
    this.dwEventService.publishEvent(
      DwUiConfigService.UICONFIG_EVENT_TOPIC.PreferencesFinalizedEvent(this.activeFormConfigKey, DwUiConfigType.form), copy);
    this.dwEventService.publishEvent(
      DwUiConfigService.UICONFIG_EVENT_TOPIC.PreferencesUpdateEvent(this.activeFormConfigKey, DwUiConfigType.form), copy);
  }

  private variantLabelFor(hierarchy: DwMetaDataFormPreferenceHierarchy): string {
    const prefs: any = this.uiConfigService.getFinalPreferences(hierarchy);
    return this.hasClass(prefs && prefs.section, this.layoutClass) ? 'Redesign' : 'Original';
  }

  private hasClass(section: any, className: string): boolean {
    if (!section) {
      return false;
    }
    if (typeof section.containerClass === 'string' && section.containerClass.split(' ').indexOf(className) >= 0) {
      return true;
    }
    const children: any[] = [...(section.sections || []), ...(section.section ? [section.section] : [])];
    return children.some(child => this.hasClass(child, className));
  }
}
